import React, { useMemo, useState } from "react";
import { buildSubmission } from "./FormSubmission";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

function FormNotice({ status, message }) {
  if (!status) return null;
  return (
    <div
      className={`form-notice ${status === "success" ? "is-success" : "is-error"}`}
      role={status === "error" ? "alert" : "status"}
      aria-live={status === "error" ? "assertive" : "polite"}
      style={{ marginTop: "2rem", color: "#EA0029" }}
    >
      {message}
    </div>
  );
}

export const VisitForm = ({ onSubmitData }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const requiredKeys = ["name", "company", "email", "message"];

  const submission = useMemo(
    () =>
      buildSubmission({
        kind: "visit",
        fields: {
          name,
          company,
          email,
          phone,
          message: projectDetails,
        },
        meta: { page: window.location.pathname },
      }),
    [name, company, email, phone, projectDetails]
  );

  const baseValidation = submission.validate(requiredKeys);
  const emailOk = email ? isEmail(email) : false;

  const errors = {
    name: baseValidation.missing.includes("name") ? "Enter your name." : "",
    company: baseValidation.missing.includes("company") ? "Enter your company name." : "",
    email: baseValidation.missing.includes("email")
      ? "Enter your email address."
      : !emailOk
      ? "Enter a valid email address."
      : "",
    message: baseValidation.missing.includes("message") ? "Write your message." : "",
  };

  const canSubmit = status.state !== "loading" && baseValidation.ok && emailOk;
  const showInvalidEmailNotice = !!email && !emailOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, company: true, email: true, message: true });

    if (!canSubmit) {
      setStatus({ state: "error", message: "Please complete the required fields before submitting." });
      return;
    }

    try {
      setStatus({ state: "loading", message: "" });

      if (typeof onSubmitData === "function") await onSubmitData(submission);

      setStatus({ state: "success", message: "Message sent successfully." });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 2000);

      setName("");
      setCompany("");
      setEmail("");
      setPhone("");
      setProjectDetails("");
      setTouched({});
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.message || "Something went wrong. Please try again.",
      });
    }
  };

  const fieldClass = (key) => (touched[key] && errors[key] ? "has-error" : "");

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="slope-contact-form">
        <div className="fields-wrapper">
          <div className="input-contact">
            <span className={`wpcf7-form-control-wrap name-field ${fieldClass("name")}`}>
              <label className="screen-reader-text" htmlFor="visit-name">Your name</label>
              <input
                id="visit-name"
                value={name}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setName(e.target.value)}
                type="text"
                name="name"
                placeholder="Your name *"
                autoComplete="name"
                required
                aria-invalid={!!(touched.name && errors.name)}
              />
            </span>

            <span className={`wpcf7-form-control-wrap company-field ${fieldClass("company")}`}>
              <label className="screen-reader-text" htmlFor="visit-company">Your company</label>
              <input
                id="visit-company"
                value={company}
                onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                onChange={(e) => setCompany(e.target.value)}
                type="text"
                name="company"
                placeholder="Your company *"
                autoComplete="organization"
                required
                aria-invalid={!!(touched.company && errors.company)}
              />
            </span>

            <span className={`wpcf7-form-control-wrap email-field ${fieldClass("email")}`}>
              <label className="screen-reader-text" htmlFor="visit-email">Email address</label>
              <input
                id="visit-email"
                value={email}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                name="email"
                placeholder="Email address *"
                autoComplete="email"
                required
                aria-invalid={!!(touched.email && errors.email)}
              />
            </span>

            <span className="wpcf7-form-control-wrap number-field">
              <label className="screen-reader-text" htmlFor="visit-phone">Phone number</label>
              <input
                id="visit-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
                name="phone"
                placeholder="Phone"
                autoComplete="tel"
              />
            </span>
          </div>

          <div className="textarea-contact">
            <span className={`wpcf7-form-control-wrap message-field ${fieldClass("message")}`}>
              <label className="screen-reader-text" htmlFor="visit-message">Write your message</label>
              <textarea
                id="visit-message"
                value={projectDetails}
                onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                onChange={(e) => setProjectDetails(e.target.value)}
                name="message"
                cols="40"
                rows="10"
                placeholder="Write your message *"
                required
                aria-invalid={!!(touched.message && errors.message)}
              />
            </span>
          </div>
        </div>

        <div className="submit-contact">
          <input
            type="submit"
            value={status.state === "loading" ? "Sending..." : status.state === "success" ? "Sent" : "Submit"}
            disabled={!canSubmit}
            style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
          />
          {showInvalidEmailNotice ? (
            <span id="visit-email-invalid" style={{ marginLeft: "1rem", color: "#EA0029" }}>Email is invalid.</span>
          ) : null}
          <FormNotice
            status={status.state === "success" ? "success" : status.state === "error" ? "error" : ""}
            message={status.message}
          />
        </div>
      </div>
    </form>
  );
};
